/**
 * Round-04 idea routes (L2, api/src/registry/graph-routes.ts, api/src/planning/version-routes.ts,
 * api/src/comments/notification-routes.ts): the one-hop graph + hierarchy (#73/#166), plan
 * history and named plan versions (#156), people search and notifications (#76). Every route
 * answers 404 `not_found` naming the setting when its flag is off; the UI never calls one then,
 * because it reads the same flag first (components/ideas/flags.ts).
 */
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, newCorrelationId } from '@/api/client';

type Raw = Record<string, unknown>;
const str = (v: unknown, d = ''): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : d);
const strOrNull = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);
const num = (v: unknown): number | null => {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
};
const arr = (v: unknown): Raw[] => (Array.isArray(v) ? (v.filter((x) => x && typeof x === 'object') as Raw[]) : []);
const obj = (v: unknown): Raw => (v && typeof v === 'object' ? (v as Raw) : {});
const enc = encodeURIComponent;

// ───────────────────────────────────────────── graph (#73) + hierarchy (#166)

export interface GraphNode {
  id: string;
  key: string;
  name: string;
  entityType: string;
  /** Current maturity 0–5 on a capability (joined from the capability index); null otherwise. */
  maturity: number | null;
}

export interface GraphEdge {
  fromId: string;
  toId: string;
  relTypeCode: string;
  /** The relationship's label read from `fromId` to `toId` ("Advances"). */
  label: string;
}

export interface EntityGraph {
  centerId: string;
  nodes: GraphNode[];
  edges: GraphEdge[];
  /** Links the server did not return (its 200-edge cap), for "N more in List". */
  hidden: number;
}

export function normalizeNode(r: Raw): GraphNode {
  return { id: str(r.id), key: str(r.key), name: str(r.name) || str(r.key), entityType: str(r.type ?? r.entityType), maturity: null };
}

export function normalizeGraph(raw: Raw): EntityGraph {
  const center = normalizeNode(obj(raw.center));
  const nodes = arr(raw.nodes).map(normalizeNode);
  if (center.id && !nodes.some((n) => n.id === center.id)) nodes.unshift(center);
  const edges = arr(raw.edges).map((e) => ({
    fromId: str(e.from),
    toId: str(e.to),
    relTypeCode: str(e.relType),
    label: str(e.label) || str(e.relType).toLowerCase().replaceAll('_', ' '),
  }));
  return { centerId: center.id, nodes, edges, hidden: num(raw.hidden) ?? 0 };
}

export function useEntityGraph(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['cigs', 'ideas', 'graph', id],
    enabled: enabled && Boolean(id),
    queryFn: async () => normalizeGraph(await apiFetch<Raw>(`/entities/${enc(id as string)}/graph?depth=1`)),
    staleTime: 60_000,
  });
}

export interface HierarchyNode extends GraphNode {
  children: HierarchyNode[];
  /** Every capability under this one that the read reached (or its direct count at the depth cap). */
  descendantCount: number;
}

export interface EntityHierarchy {
  /** Root first, the record's own parent last. */
  ancestors: GraphNode[];
  self: GraphNode | null;
  descendants: HierarchyNode[];
  totalDescendants: number;
  /** True when the tree was cut at the depth cap — deeper levels exist. */
  truncated: boolean;
}

function normalizeTree(r: Raw): HierarchyNode {
  const children = arr(r.children).map(normalizeTree);
  const below = children.reduce((n, c) => n + 1 + c.descendantCount, 0);
  return { ...normalizeNode(r), children, descendantCount: children.length ? below : (num(r.childCount) ?? 0) };
}

export function normalizeHierarchy(raw: Raw): EntityHierarchy {
  const descendants = arr(raw.descendants).map(normalizeTree);
  return {
    ancestors: arr(raw.ancestors).map(normalizeNode),
    self: raw.entity ? normalizeNode(obj(raw.entity)) : null,
    descendants,
    totalDescendants: num(raw.totalDescendants) ?? descendants.reduce((n, c) => n + 1 + c.descendantCount, 0),
    truncated: raw.truncated === true,
  };
}

export function useHierarchy(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['cigs', 'ideas', 'hierarchy', id],
    enabled: enabled && Boolean(id),
    queryFn: async () => normalizeHierarchy(await apiFetch<Raw>(`/entities/${enc(id as string)}/hierarchy?depth=5`)),
    staleTime: 60_000,
  });
}

// ───────────────────────────────────────────── plan history + versions (#156)

export type PlanHistoryKind = 'edit' | 'undo' | 'restore' | 'capture';

export interface PlanHistoryEntry {
  /** change_log id — the same id as `UndoOffer.receiptId` for the edit Undo would reverse. */
  id: string;
  at: string;
  actorName: string;
  actorUserId: string | null;
  kind: PlanHistoryKind;
  /** In words, from the receipt: "move Stand up Clinical Supply to Q2 FY2027". */
  summary: string;
  reason: string | null;
  initiativeId: string | null;
  initiativeKey: string | null;
  initiativeName: string | null;
  /** This receipt reverses `undoOf`. */
  undoOf: string | null;
  /** This receipt was reversed by `undoneBy`. */
  undoneBy: string | null;
}

export function normalizeHistoryEntry(r: Raw): PlanHistoryEntry {
  const actor = obj(r.actor);
  const initiative = r.initiative ? obj(r.initiative) : null;
  const kind = str(r.kind, 'edit');
  return {
    id: str(r.id),
    at: str(r.at),
    actorName: str(actor.name, 'Someone') || 'Someone',
    actorUserId: strOrNull(actor.id),
    kind: (['edit', 'undo', 'restore', 'capture'].includes(kind) ? kind : 'edit') as PlanHistoryKind,
    summary: str(r.summary),
    reason: strOrNull(r.reason),
    initiativeId: initiative ? strOrNull(initiative.id) : null,
    initiativeKey: initiative ? strOrNull(initiative.key) : null,
    initiativeName: initiative ? strOrNull(initiative.name) : null,
    undoOf: strOrNull(r.undoOf),
    undoneBy: strOrNull(r.undoneBy),
  };
}

export const ideasKeys = {
  planHistory: (scopeId: string) => ['cigs', 'ideas', 'plan-history', scopeId] as const,
  planVersions: (scopeId: string) => ['cigs', 'ideas', 'plan-versions', scopeId] as const,
  compare: (scopeId: string, a: string, b: string) => ['cigs', 'ideas', 'plan-compare', scopeId, a, b] as const,
  notifications: ['cigs', 'ideas', 'notifications'] as const,
};

/** Newest first (the server's order), 50 at a time. */
export function usePlanHistory(scopeId: string, enabled: boolean) {
  return useQuery({
    queryKey: ideasKeys.planHistory(scopeId),
    enabled,
    queryFn: async () => {
      const raw = await apiFetch<Raw>(`/planning/scopes/${enc(scopeId)}/history?limit=50`);
      return { items: arr(raw.items).map(normalizeHistoryEntry), nextBefore: strOrNull(raw.nextBefore) };
    },
  });
}

export interface PlanVersion {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
  createdByName: string;
  initiativeCount: number;
  /** currency → amount (decimal string, major units). Never summed across currencies. */
  totalsByCurrency: Record<string, string>;
  /** The scope's plan today is exactly this version. */
  isCurrent: boolean;
}

export function normalizeVersion(r: Raw): PlanVersion {
  const totals: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj(r.totalsByCurrency))) totals[k] = str(v);
  return {
    id: str(r.id),
    name: str(r.name),
    description: strOrNull(r.description),
    createdAt: str(r.createdAt),
    createdByName: str(obj(r.createdBy).name) || 'Seeded',
    initiativeCount: num(r.initiativeCount) ?? 0,
    totalsByCurrency: totals,
    isCurrent: r.isCurrent === true,
  };
}

export interface PlanVersionList {
  /** Pass as `expectedRevision` to restore (PLANNING-CONTRACT §4: stale → 409). */
  currentRevision: string;
  versions: PlanVersion[];
}

export function usePlanVersions(scopeId: string, enabled: boolean) {
  return useQuery({
    queryKey: ideasKeys.planVersions(scopeId),
    enabled,
    queryFn: async (): Promise<PlanVersionList> => {
      const raw = await apiFetch<Raw>(`/planning/scopes/${enc(scopeId)}/versions`);
      return { currentRevision: str(raw.currentRevision), versions: arr(raw.versions).map(normalizeVersion) };
    },
  });
}

export function useSavePlanVersion(scopeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name: string; description?: string }) =>
      normalizeVersion(
        await apiFetch<Raw>(`/planning/scopes/${enc(scopeId)}/versions`, { method: 'POST', body, correlationId: newCorrelationId() }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ideasKeys.planVersions(scopeId) });
      void qc.invalidateQueries({ queryKey: ideasKeys.planHistory(scopeId) });
    },
  });
}

/** One side's value of a field: `a` = the left side, `b` = the right side (decimal/date strings). */
export interface PlanFieldDiff {
  field: string;
  a: string | null;
  b: string | null;
}

export interface PlanInitiativeDiff {
  initiativeId: string;
  initiativeKey: string;
  initiativeName: string;
  change: 'added' | 'removed' | 'changed' | 'unchanged';
  fields: PlanFieldDiff[];
  allocations: Array<{ month: string; a: string | null; b: string | null; aManual: boolean; bManual: boolean }>;
  advances: Array<{
    capabilityId: string;
    capabilityKey: string;
    capabilityName: string;
    a: { toMaturity: number | null; isPrimary: boolean } | null;
    b: { toMaturity: number | null; isPrimary: boolean } | null;
  }>;
  allocated: { a: string | null; b: string | null; currency: string | null };
}

export interface MoneyDelta {
  currency: string;
  a: string;
  b: string;
  delta: string;
}

export interface PlanCompare {
  a: { id: string; name: string; capturedAt: string };
  b: { id: string; name: string; capturedAt: string };
  initiatives: PlanInitiativeDiff[];
  totalsByCurrency: MoneyDelta[];
  totalsByFiscalYear: Array<MoneyDelta & { fiscalYear: string }>;
  counts: { added: number; removed: number; changed: number; unchanged: number };
}

const side = (v: unknown) => (v && typeof v === 'object' ? { toMaturity: num((v as Raw).toMaturity), isPrimary: (v as Raw).isPrimary === true } : null);
const money = (m: Raw): MoneyDelta => ({ currency: str(m.currency), a: str(m.a, '0'), b: str(m.b, '0'), delta: str(m.delta, '0') });

export function normalizeCompare(raw: Raw): PlanCompare {
  const sideMeta = (v: unknown) => {
    const o = obj(v);
    return { id: str(o.id), name: str(o.name), capturedAt: str(o.capturedAt) };
  };
  const counts = obj(raw.counts);
  return {
    a: sideMeta(raw.a),
    b: sideMeta(raw.b),
    initiatives: arr(raw.initiatives).map((i) => ({
      initiativeId: str(i.initiativeId),
      initiativeKey: str(i.initiativeKey),
      initiativeName: str(i.initiativeName),
      change: (['added', 'removed', 'changed', 'unchanged'].includes(str(i.change)) ? str(i.change) : 'unchanged') as PlanInitiativeDiff['change'],
      fields: arr(i.fields).map((f) => ({ field: str(f.field), a: strOrNull(f.a), b: strOrNull(f.b) })),
      allocations: arr(i.allocations).map((a) => ({
        month: str(a.month),
        a: strOrNull(a.a),
        b: strOrNull(a.b),
        aManual: a.aManual === true,
        bManual: a.bManual === true,
      })),
      advances: arr(i.advances).map((a) => ({
        capabilityId: str(a.capabilityId),
        capabilityKey: str(a.capabilityKey),
        capabilityName: str(a.capabilityName),
        a: side(a.a),
        b: side(a.b),
      })),
      allocated: { a: strOrNull(obj(i.allocated).a), b: strOrNull(obj(i.allocated).b), currency: strOrNull(obj(i.allocated).currency) },
    })),
    totalsByCurrency: arr(raw.totalsByCurrency).map(money),
    totalsByFiscalYear: arr(raw.totalsByFiscalYear).map((m) => ({ ...money(m), fiscalYear: str(m.fiscalYear) })),
    counts: {
      added: num(counts.added) ?? 0,
      removed: num(counts.removed) ?? 0,
      changed: num(counts.changed) ?? 0,
      unchanged: num(counts.unchanged) ?? 0,
    },
  };
}

/** `a`, `b` = a version id or 'current' (the live plan). */
export function usePlanCompare(scopeId: string, a: string | null, b: string | null) {
  return useQuery({
    queryKey: ideasKeys.compare(scopeId, a ?? '', b ?? ''),
    enabled: Boolean(a && b && a !== b),
    queryFn: async () =>
      normalizeCompare(await apiFetch<Raw>(`/planning/scopes/${enc(scopeId)}/versions/${enc(a as string)}/compare/${enc(b as string)}`)),
  });
}

export interface PlanRestoreResult {
  replayed: boolean;
  restored: Array<{ initiativeId: string; initiativeKey: string; initiativeName: string; receiptId: string }>;
  unchanged: string[];
  skipped: Array<{ initiativeId: string; initiativeKey: string; reason: string }>;
  notInVersion: string[];
}

/**
 * Restore = ordinary audited plan edits (Undo still reverses each). `expectedRevision` is the
 * list's `currentRevision`; a stale one is a 409 the caller shows as "the plan changed — refresh".
 * The operation id is minted once per confirmation, so a retried click replays, never doubles.
 */
export function useRestorePlanVersion(scopeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { versionId: string; expectedRevision: string; operationId: string }): Promise<PlanRestoreResult> => {
      const raw = await apiFetch<Raw>(`/planning/scopes/${enc(scopeId)}/versions/${enc(body.versionId)}/restore`, {
        method: 'POST',
        body: { expectedRevision: body.expectedRevision, operationId: body.operationId },
        correlationId: body.operationId,
      });
      return {
        replayed: raw.replayed === true,
        restored: arr(raw.restored).map((r) => ({
          initiativeId: str(r.initiativeId),
          initiativeKey: str(r.initiativeKey),
          initiativeName: str(r.initiativeName),
          receiptId: str(r.receiptId),
        })),
        unchanged: (Array.isArray(raw.unchanged) ? raw.unchanged : []).map((x) => str(x)),
        skipped: arr(raw.skipped).map((s) => ({ initiativeId: str(s.initiativeId), initiativeKey: str(s.initiativeKey), reason: str(s.reason) })),
        notInVersion: (Array.isArray(raw.notInVersion) ? raw.notInVersion : []).map((x) => str(x)),
      };
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ideasKeys.planHistory(scopeId) });
      void qc.invalidateQueries({ queryKey: ideasKeys.planVersions(scopeId) });
      void qc.invalidateQueries({ queryKey: ['cigs', 'planning'] });
    },
  });
}

// ───────────────────────────────────────────── people + notifications (#76)

export interface Person {
  /** app_user id — what `comment.mentions` carries. */
  id: string;
  displayName: string;
  roleTitle: string | null;
}

export function usePeopleSearch(q: string, enabled: boolean) {
  return useQuery({
    queryKey: ['cigs', 'ideas', 'people', q],
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<Person[]> => {
      const raw = await apiFetch<Raw>(`/people?q=${enc(q)}&limit=20`);
      return arr(raw.people).map((p) => ({ id: str(p.id), displayName: str(p.displayName), roleTitle: strOrNull(p.roleTitle) }));
    },
  });
}

export interface AppNotification {
  id: string;
  kind: string;
  at: string;
  actorName: string;
  /** null when the entity is gone; `entityName` null when you may no longer read it. */
  entityId: string | null;
  entityType: string;
  entityKey: string | null;
  entityName: string | null;
  /** null when you may not read the entity — a mention never grants access. */
  excerpt: string | null;
  read: boolean;
}

export function normalizeNotification(r: Raw): AppNotification {
  const entity = r.entity ? obj(r.entity) : null;
  return {
    id: str(r.id),
    kind: str(r.kind, 'mention'),
    at: str(r.createdAt),
    actorName: str(obj(r.actor).name) || 'Someone',
    entityId: entity ? strOrNull(entity.id) : null,
    entityType: entity ? str(entity.type) : '',
    entityKey: entity ? strOrNull(entity.key) : null,
    entityName: entity ? strOrNull(entity.name) : null,
    excerpt: r.excerptHidden === true ? null : strOrNull(r.excerpt),
    read: Boolean(r.readAt),
  };
}

/** One page of the list (newest 30) plus the server's own unread count — the badge's only source. */
export async function fetchNotifications(): Promise<{ items: AppNotification[]; unread: number }> {
  const raw = await apiFetch<Raw>('/me/notifications?limit=30');
  const items = arr(raw.notifications).map(normalizeNotification);
  return { items, unread: num(raw.unreadCount) ?? items.filter((n) => !n.read).length };
}

export function useNotifications(enabled: boolean) {
  return useQuery({
    queryKey: ideasKeys.notifications,
    enabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    queryFn: fetchNotifications,
  });
}

/**
 * "Mark all read" (#76, PR #14 review): ONE `POST /me/notifications/read-all`, which marks every
 * unread notification server-side — not just the 30 the list fetched — then re-reads the list
 * and the count. The badge is set from the server's answer at once, so it never lags a refetch.
 */
export async function markAllNotificationsRead(qc: QueryClient): Promise<void> {
  const raw = await apiFetch<Raw>('/me/notifications/read-all', { method: 'POST' });
  const unread = num(raw?.unreadCount) ?? 0;
  qc.setQueryData<{ items: AppNotification[]; unread: number }>(ideasKeys.notifications, (prev) =>
    prev ? { items: prev.items.map((n) => ({ ...n, read: true })), unread } : prev,
  );
  await qc.invalidateQueries({ queryKey: ideasKeys.notifications });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: () => markAllNotificationsRead(qc) });
}

/** The bell's badge text from the server's unread count: hidden at 0, capped at 9+. */
export function notificationBadge(unread: number): string | null {
  if (unread <= 0) return null;
  return unread > 9 ? '9+' : String(unread);
}

/** Marks each id read (the API reads one at a time: POST /me/notifications/{id}/read). */
export function useMarkNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map((id) => apiFetch<unknown>(`/me/notifications/${enc(id)}/read`, { method: 'POST' })));
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ideasKeys.notifications }),
  });
}
